import React, { useState, useEffect, useRef, useCallback } from 'react';
import { US_STATES } from '../../utils/addressUtils';
import './StateSelector.css';

const StateSelector = ({ value, onChange, name, id, required, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Find the full name for the initial value
  const getInitialLabel = useCallback(() => {
    if (!value) return '';
    const stateEntry = Object.entries(US_STATES).find(([_, code]) => code === value);
    return stateEntry ? stateEntry[0] : '';
  }, [value]);

  const [displayValue, setDisplayValue] = useState(getInitialLabel());

  useEffect(() => {
    setDisplayValue(getInitialLabel());
  }, [getInitialLabel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset display value to the selected state if search was cancelled
        setDisplayValue(getInitialLabel());
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [getInitialLabel]);

  const filteredStates = Object.entries(US_STATES).filter(([fullName, code]) =>
    fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputClick = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setDisplayValue(val);
    setSearchTerm(val);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelectState = (fullName, code) => {
    setDisplayValue(fullName);
    setIsOpen(false);
    // Call the parent's onChange with a synthetic event
    onChange({
      target: {
        name: name,
        value: code
      }
    });
  };

  return (
    <div className={`state-selector-container ${className || ''}`} ref={dropdownRef}>
      <div className="state-input-wrapper">
        <input
          type="text"
          id={id}
          name={name}
          ref={inputRef}
          value={displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onFocus={handleInputClick}
          placeholder="Search or select state"
          required={required}
          className="form-input state-search-input"
          autoComplete="off"
        />
        <div className={`state-dropdown-arrow ${isOpen ? 'open' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="state-dropdown-list">
          {filteredStates.length > 0 ? (
            filteredStates.map(([fullName, code]) => (
              <div
                key={code}
                className={`state-dropdown-item ${value === code ? 'selected' : ''}`}
                onClick={() => handleSelectState(fullName, code)}
              >
                <span className="state-full-name">{fullName}</span>
                <span className="state-code">{code}</span>
              </div>
            ))
          ) : (
            <div className="state-dropdown-no-results">No states found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default StateSelector;
